import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, Form } from 'react-bootstrap';

import { loadSiteSchedulesStart } from "services/redux/actions/singleSites";

import Alert from "components/Common/Alert";
import Icon from "components/Icons/Icon";
import SVGIcon from "components/Icons/SVGIcon";
import { Tooltip } from "components/Common/Tooltip/Tooltip";

import moment from "services/locale/momentInit.js";
import { format } from 'date-fns';

import conxtdOut from "apis/conxtdOut";

export default function AddRule({ site, enterprise_id, weekdays, getFormattedIdealAndOffsets }) {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);

  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [ruleDate, setRuleDate] = useState(null);

  const [openEarlyOffsetEntered, setOpenEarlyOffsetEntered] = useState(false);
  const [openLateOffsetEntered, setOpenLateOffsetEntered] = useState(false);
  const [closeEarlyOffsetEntered, setCloseEarlyOffsetEntered] = useState(false);
  const [closeLateOffsetEntered, setCloseLateOffsetEntered] = useState(false);

  const [openEarlyOffsetTime, setOpenEarlyOffsetTime] = useState("00:00");
  const [openLateOffsetTime, setOpenLateOffsetTime] = useState("00:00");
  const [closeEarlyOffsetTime, setCloseEarlyOffsetTime] = useState("00:00");
  const [closeLateOffsetTime, setCloseLateOffsetTime] = useState("00:00");

  const [closeReminderChecked, setCloseReminderChecked] = useState(true);

  const [notExpected, setNotExpected] = useState(false);

  const [todayString, setTodayString] = useState("");
  const [todayMoment, setTodayMoment] = useState("");

  const eventCode = "C173";

  useEffect(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    setTodayString(today);
    setTodayMoment(moment(today, "YYYY-MM-DD"));
  }, []);

  const resetForm = () => {
    document.getElementById("add_schedule_rule_form").reset();
    setRuleDate(null);
    setNotExpected(false);
    setOpenEarlyOffsetEntered(false);
    setOpenLateOffsetEntered(false);
    setCloseEarlyOffsetEntered(false);
    setCloseLateOffsetEntered(false);
    setCloseReminderChecked(true);
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      if (ruleDate.isBefore(todayMoment)) {
        Alert({
          text: "Date must not be in the past.",
          icon: "warning",
          timerProgressBar: true,
          timer: 3000,
          showConfirmButton: true,
        });
      } else {
        let ruleWeekday = null;
        let ruleWeekdayName = "";
        if (ruleDate) {
          ruleWeekday = ruleDate.day();
          ruleWeekdayName = weekdays[(ruleWeekday === 0 ? 6 : ruleWeekday-1)]; // moment uses sunday=0 ... saturday=6, but weekdays array uses monday=0 ... sunday=6 
          ruleWeekday = ruleWeekday === 0 ? 7 : ruleWeekday; // moment uses sunday=0 ... saturday=6, but backend uses monday=1 ... sunday=7 
        }

        const ruleDateYear = String(event.target.rule_date.value).substring(0, 4);
        const ruleDateMonth = String(event.target.rule_date.value).substring(5, 7);
        const ruleDateDay = String(event.target.rule_date.value).substring(8, 10);

        const addEventUrl = enterprise_id
          ? `/Events/add/${site.id}`
          : `/Events/add/${site.id}/ss`;

        if (notExpected) {
          const notExpectedParams = {
            rule_type_id: 4,
            sudo_site_id: site.sudo_site_id,

            start_date: event.target.rule_date.value,
            end_date: event.target.rule_date.value,
            start_day_of_week: ruleWeekday,
            end_day_of_week: ruleWeekday,
          }
          if (enterprise_id) {
            notExpectedParams["enterprise_id"] = enterprise_id;
          }

          conxtdOut.post(`/analyticx/scheduler-rules/add`, notExpectedParams)
          .then(() => {
            Alert({
              text: "Rules have been added.",
              icon: "success",
              timerProgressBar: true,
              timer: 3000,
              showConfirmButton: true,
            });

            resetForm();

            dispatch(loadSiteSchedulesStart({
              sudo_site_id: site.sudo_site_id,
              enterprise_id: enterprise_id,
            }));

            // inject event to say user has added rule
            try {
              conxtdOut.post(addEventUrl, {
                event: eventCode,
                zone: "",
                text: `${user.firstName + " " + user.lastName} added Temporary Schedule for ${ruleWeekdayName} ${ruleDateDay}/${ruleDateMonth}/${ruleDateYear} Not Expected.`,
                time: moment.utc().format(),
              }).catch(error => {
                console.log(error);
              });
            } catch (error) {
              console.log(error);
              }
          })
          .catch((error) => {
            console.error("error:", error.message);
            Alert({
              text: "Error encountered while adding rule.",
              icon: "error",
              timerProgressBar: true,
              timer: 3000,
            });
          })
          
        } else {
          const requestsDetails = [];

          if (event.target.open_ideal_event_hour.value) {
            const openParams = {
              rule_type_id: 1,
              sudo_site_id: site.sudo_site_id,

              start_date: event.target.rule_date.value,
              end_date: event.target.rule_date.value,
              start_day_of_week: ruleWeekday,
              end_day_of_week: ruleWeekday,
              ideal_event_hour: event.target.open_ideal_event_hour.value,
              early_offset: event.target.open_early_offset.value,
              late_offset: event.target.open_late_offset.value,
              // reminder: event.target.open_reminder.checked,
            }
            if (enterprise_id) {
              openParams["enterprise_id"] = enterprise_id;
            }
            requestsDetails.push({url: `/analyticx/scheduler-rules/add`, params: openParams, ruleTypeName: "Open" });
          }

          if (event.target.close_ideal_event_hour.value) {
            const closeParams = {
              rule_type_id: 2,
              sudo_site_id: site.sudo_site_id,

              start_date: event.target.rule_date.value,
              end_date: event.target.rule_date.value,
              start_day_of_week: ruleWeekday,
              end_day_of_week: ruleWeekday,
              ideal_event_hour: event.target.close_ideal_event_hour.value,
              early_offset: event.target.close_early_offset.value,
              late_offset: event.target.close_late_offset.value,
              reminder: event.target.close_reminder.checked,
            }
            if (enterprise_id) {
              closeParams["enterprise_id"] = enterprise_id;
            }
            requestsDetails.push({url: `/analyticx/scheduler-rules/add`, params: closeParams, ruleTypeName: "Close" });
          }

          if (requestsDetails.length > 0) {
            Promise.allSettled(requestsDetails.map((requestDetails) => {
              return conxtdOut.post(requestDetails.url, requestDetails.params);
            }))
            .then((results) => {
              // check if the requests were successful
              if (results.every((result) => result.status === "fulfilled")) {
                Alert({
                  text: "Rules added successfully.",
                  icon: "success",
                  timerProgressBar: true,
                  timer: 3000,
                  showConfirmButton: true,
                });

                resetForm();

                dispatch(loadSiteSchedulesStart({
                  sudo_site_id: site.sudo_site_id,
                  enterprise_id: enterprise_id,
                }));
              } else {
                // at least one of the requests failed
                Alert({
                  text: "Failed to add rule/s.",
                  icon: "error",
                  timerProgressBar: true,
                  timer: 3000,
                });
              }

              // inject event/s to say user has added rule/s
              requestsDetails.forEach((requestDetails, index) => {
                if (results[index].status === "fulfilled") {
                  let addEventText = `${user.firstName + " " + user.lastName}`;
                  addEventText = addEventText + " added Temporary Schedule for ";
                  addEventText = addEventText + `${ruleWeekdayName} ${ruleDateDay}/${ruleDateMonth}/${ruleDateYear} `;
                  addEventText = addEventText + `${requestDetails.ruleTypeName} at ${requestDetails.params.ideal_event_hour}.`;

                  try {
                    conxtdOut.post(addEventUrl, {
                      event: eventCode,
                      zone: "",
                      text: addEventText,
                      time: moment.utc().format(),
                    }).catch(error => {
                      console.log(error);
                    });
                  } catch (error) {
                    console.log(error);
                  }
                }
              })
            })
            .catch((error) => {
              console.error("error:", error.message);
              Alert({
                text: "Error encountered while adding rule/s.",
                icon: "error",
                timerProgressBar: true,
                timer: 3000,
              });
            });
          } else {
            Alert({
              text: "No rules entered.",
              icon: "info",
              timerProgressBar: true,
              timer: 3000,
            });
          }
        }
      }
    } catch (error) {
      Alert({
        text: error.response.data.message,
        icon: "error",
      });
    }
  };

  const handleClose = () => {
    setShowAddRuleModal(false);
  };
  const handleShow = () => {
    setShowAddRuleModal(true);
  };  

  return (
    <>
      <Tooltip
        description="Add Rules"
      >
        <Button variant="primary" onClick={handleShow}>
          <Icon
            className="fas fa-plus"
          />
        </Button>
      </Tooltip>
        <div>
          <Modal
            show={showAddRuleModal}
            onHide={handleClose}
          >
            <Form id="add_schedule_rule_form" onSubmit={event => handleSubmit(event)}>
              <Modal.Header closeButton>
                <Modal.Title>Add Rule</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="mb-3">

                  {/* date */}
                  <div className="d-flex align-items-start" style={{ flexWrap: "wrap" }}>
                    <div className="pr-2 mb-4 d-flex align-items-center">
                      <Form.Group
                        controlId="start_date"
                        className="mb-0 pr-2"
                      >
                        <Form.Label className="pl-1">Date</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="date"
                            name="rule_date"
                            required
                            min={todayString}
                            onChange={(event) => {
                              const ruleDate = new moment(event.target.value, "YYYY-MM-DD");
                              setRuleDate(ruleDate);
                            }}
                          />
                          <div className="ml-2 flex-shrink-0 font-weight-bold" style={{ fontSize: "1rem" }}>
                            {ruleDate && ruleDate.format("dddd")}
                            </div>
                        </div>
                      </Form.Group>
                    </div>
                  </div>

                  {/* not expected */}
                  <div>
                    <Form.Group
                      controlId="not-expected"
                      className="d-flex justify-content-start align-items-center"
                    >
                      <Form.Control
                        name="not-expected"
                        style={{ width: "1rem", height: "1rem" }}
                        checked={notExpected}
                        type="checkbox"
                        onChange={event => setNotExpected(event.target.checked)}
                      />
                      <Form.Label className="mb-0 pl-2">Not Expected</Form.Label>
                    </Form.Group>
                  </div>

                  {/* rule headers */}
                  <div className="d-flex">
                    <div style={{ width: "2rem", flexShrink: "0" }}></div>{/* open/close icon placeholder */}
                    <div className="pl-3 d-flex justify-content-center align-items-center">
                      <div className="pl-1 d-flex justify-content-start" style={{ width: "5rem" }}><Form.Label>Early</Form.Label></div>{/* early offset */}
                      <div className="pl-1 d-flex justify-content-start" style={{ width: "8rem" }}><Form.Label>Ideal</Form.Label></div>{/* ideal event hour */}
                      <div className="pl-1 d-flex justify-content-start" style={{ width: "5rem" }}><Form.Label>Late</Form.Label></div>{/* late offset */}
                      <div className="pl-3 d-flex justify-content-start" style={{ width: "5rem" }}><Form.Label>Reminder</Form.Label></div>{/* reminder */}
                    </div>
                  </div>

                  {/* rules */}
                  <div className="mb-3 d-flex">
                    <div className="p-0 d-flex justify-content-center align-items-center" style={{ width: "2rem" }}>{/* open icon */}
                      <div style={{ width: "2rem", height: "2rem", paddingLeft: "2px" }}>
                        <SVGIcon type="unlock" color={"#3397DB"} />
                      </div>
                    </div>
                    <div className="ml-3 p-0" style={{ width: "5rem" }}>{/* open early offset */}
                      <Tooltip 
                        placement="top"
                        description={`Early offset time: ${openEarlyOffsetTime}`}
                      >
                        <Form.Control
                          type="number"
                          id="open_early_offset"
                          name="open_early_offset"
                          disabled={notExpected}
                          onChange={(event) => {
                            setOpenEarlyOffsetEntered(!!event.target.value);

                            const { earlyOffsetTime } = getFormattedIdealAndOffsets(
                              document.getElementById("open_ideal_event_hour").value,
                              event.target.value,
                              document.getElementById("open_late_offset").value,
                            );
                            setOpenEarlyOffsetTime(earlyOffsetTime);
                          }}
                        />
                      </Tooltip>
                    </div>
                    <div className="px-2 py-0" style={{ width: "8rem" }}>{/* open ideal event hour */}
                      <Form.Control
                        type="time"
                        id="open_ideal_event_hour"
                        name="open_ideal_event_hour"
                        disabled={notExpected}
                        required={(!notExpected) && (openEarlyOffsetEntered || openLateOffsetEntered)}
                        onChange={(event) => {
                          const { earlyOffsetTime, lateOffsetTime } = getFormattedIdealAndOffsets(
                              event.target.value,
                              document.getElementById("open_early_offset").value,
                              document.getElementById("open_late_offset").value,
                            );
                            setOpenEarlyOffsetTime(earlyOffsetTime);
                            setOpenLateOffsetTime(lateOffsetTime);
                        }}
                      />
                    </div>
                    <div className="p-0" style={{ width: "5rem" }}>{/* open late offset */}
                      <Tooltip 
                        placement="top"
                        description={`Late offset time: ${openLateOffsetTime}`}
                      >
                        <Form.Control
                          type="number"
                          id="open_late_offset"
                          name="open_late_offset"
                          disabled={notExpected}
                          onChange={(event) => {
                            setOpenLateOffsetEntered(!!event.target.value);

                            const { lateOffsetTime } = getFormattedIdealAndOffsets(
                              document.getElementById("open_ideal_event_hour").value,
                              document.getElementById("open_early_offset").value,
                              event.target.value,
                            );
                            setOpenLateOffsetTime(lateOffsetTime);
                          }}
                        />
                      </Tooltip>
                    </div>
                    <div className="pl-3 py-0 d-flex justify-content-start align-items-center" style={{ width: "5rem" }}>{/* reminder */}
                      {/* <Form.Group // if you need to reinstate this field, remember to reinstate it in handleSubmit too
                        controlId="open_reminder"
                        className="mb-0 d-flex align-items-center"
                      >
                        <Form.Control
                          name="open_reminder"
                          style={{ width: "1rem", height: "1rem" }}
                          type="checkbox"
                          disabled={notExpected}
                        />
                      </Form.Group> */}
                    </div>
                  </div>
                  <div className="mb-3 d-flex">
                    <div className="p-0 d-flex justify-content-center align-items-center" style={{ width: "2rem" }}>{/* close icon */}
                      <div style={{ width: "1.7rem", height: "1.7rem" }}>
                        <SVGIcon type="lock" color={"#28498A"} />
                      </div>
                    </div>
                    <div className="ml-3 p-0" style={{ width: "5rem" }}>{/* close early offset */}
                      <Tooltip 
                        placement="top"
                        description={`Early offset time: ${closeEarlyOffsetTime}`}
                      >
                        <Form.Control
                          type="number"
                          id="close_early_offset"
                          name="close_early_offset"
                          onChange={(event) => {
                            setCloseEarlyOffsetEntered(!!event.target.value);

                            const { earlyOffsetTime } = getFormattedIdealAndOffsets(
                              document.getElementById("close_ideal_event_hour").value,
                              event.target.value,
                              document.getElementById("close_late_offset").value,
                            );
                            setCloseEarlyOffsetTime(earlyOffsetTime);
                          }}
                          disabled={notExpected}
                        />
                      </Tooltip>
                    </div>
                    <div className="px-2 py-0" style={{ width: "8rem" }}>{/* close ideal event hour */}
                      <Form.Control
                        type="time"
                        id="close_ideal_event_hour"
                        name="close_ideal_event_hour"
                        disabled={notExpected}
                        required={(!notExpected) && (closeEarlyOffsetEntered || closeLateOffsetEntered)}
                        onChange={(event) => {
                          const { earlyOffsetTime, lateOffsetTime } = getFormattedIdealAndOffsets(
                              event.target.value,
                              document.getElementById("close_early_offset").value,
                              document.getElementById("close_late_offset").value,
                            );
                            setCloseEarlyOffsetTime(earlyOffsetTime);
                            setCloseLateOffsetTime(lateOffsetTime);
                        }}
                      />
                    </div>
                    <div className="p-0" style={{ width: "5rem" }}>{/* close late offset */}
                      <Tooltip 
                        placement="top"
                        description={`Late offset time: ${closeLateOffsetTime}`}
                      >
                        <Form.Control
                          type="number"
                          id="close_late_offset"
                          name="close_late_offset"
                          disabled={notExpected}
                          onChange={(event) => {
                            setCloseLateOffsetEntered(!!event.target.value);

                            const { lateOffsetTime } = getFormattedIdealAndOffsets(
                              document.getElementById("close_ideal_event_hour").value,
                              document.getElementById("close_early_offset").value,
                              event.target.value,
                            );
                            setCloseLateOffsetTime(lateOffsetTime);
                          }}
                        />
                      </Tooltip>
                    </div>
                    <div className="pl-3 py-0 d-flex justify-content-start align-items-center" style={{ width: "5rem" }}>{/* reminder */}
                      <Form.Group
                        controlId="close_reminder"
                        className="mb-0 d-flex align-items-center"
                      >
                        <Form.Control
                          name="close_reminder"
                          style={{ width: "1rem", height: "1rem" }}
                          type="checkbox"
                          checked={closeReminderChecked}
                          onChange={(event) => {
                            setCloseReminderChecked(event.target.checked)
                          }}
                          disabled={notExpected}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <div className="container">
                  <div className="d-flex justify-content-end">
                    {/* <Button
                      className="mr-auto p-2"
                      variant="danger"
                      onClick={this.handleDelete}
                    >
                      Delete
                    </Button> */}
                    <Button
                      variant="primary"
                      className="mr-2"
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                    <Button variant="success" type="submit">
                      Add Rule
                    </Button>
                  </div>
                </div>
              </Modal.Footer>
            </Form>
          </Modal>
        </div>
    </>
  );
};